import React, { PureComponent } from 'react';
import { ICONS } from '../Icon/Icon';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';

/**
 * `Progressbar` is a component that shows progress percentage between 0 and 100%.
 *
 * The component supports both linear and circular visualizations, and showing an optional percentage/label value. Value
 * changes are animated.
 *
 * The component also supports an *indeterminate* state for cases where progress is unknown.
 *
 * Multiple values can be shown in the same progress bar by setting the *marked* prop. This can be useful when you for
 * instance want to show the total space available and the amount of used and free space within the available space.
 *
 * @property {boolean} circular If set to `true`, the progress bar will be circular with the percentage text in the middle
 * of the circle if *showProgress* is also `true`. If set to `false`, the progress bar will be linear. Defaults to `false`
 * (linear).
 * @property {string} className The class name for this component. Will be appended to "core-progressbar".
 * @property {string} icon Displays any of the bundled icons by *name*. Only valid when *showLabel* is set to `false` and *circular* is set to `true`.
 * @property {boolean} indeterminate If set to `true` the progress will be indeterminate and keep animating regardless of
 * what *value* is.
 * @property {string} label Label to show instead of the percentage. Only valid when *showLabel* is set to `true`. Defaults
 * to no label (percentage will be shown).
 * @property {number} marked If set to a value between 1 and 100, the progress bar will show a marked area in the progress
 * bar container. You may use this to display multiple values within the same progress bar. E.g. setting this value to 80
 * will mark 80% of the progress bar container. Only works for non circular progress bars. Defaults to no marked area.
 * @property {boolean} showLabel If set to `true`, the percentage will be shown to the right of the progress bar if
 * *circular* is `false`, and in the middle of the circle if *circular* is `true`. Defaults to `true`.
 * @property {number} value The percentage completed. Should be a number between 0 and 100. Values outside this
 * range will be capped.
 * @property {number} width The width of the circular progress bar. Only applies when *circular* is set to `true`. Defaults
 * to 50 (pixels).
 */
export default class Progressbar extends PureComponent {

  static displayName = 'Progressbar';

  static propTypes = {
    circular: PropTypes.bool,
    className: PropTypes.string,
    icon: PropTypes.string,
    indeterminate: PropTypes.bool,
    label: PropTypes.string,
    showLabel: PropTypes.bool,
    value: PropTypes.number,
    marked: PropTypes.number,
    width: PropTypes.number,
  };

  static defaultProps = {
    showLabel: true,
    value: 0,
    width: 50,
  };

  render() {
    const { circular, className, icon, indeterminate, label, marked, showLabel, value, width } = this.props;
    const percent = Math.max(0, Math.min(Math.floor(value || 0), 100));
    const percentageText = `${percent}%`;
    let secondary;

    if (circular) {
      const radius = (100 / 2) - 7; // SVG viewBox width / 2 - strokeWidth
      const dashArray = radius * Math.PI * 2;

      if (indeterminate) {
        return (
          <div className={cx(`core-progressbar circular indeterminate`, className)}>
            <svg role="img" aria-label="Progressbar" className="progress-circle" viewBox="0 0 100 100" style={{ width }}>
              <circle className="core-progressbar-progress" cx="50" cy="50" r={radius}/>
            </svg>
          </div>
        );
      }
      return (
        <svg role="img" aria-label="Progressbar" className={cx('core-progressbar circular', className, { full: percent === 100 })} viewBox="0 0 100 100" style={{ width }}>
          <circle className="core-progressbar-container" cx="50" cy="50" r={radius}/>
          <circle className="core-progressbar-progress" cx="50" cy="50" r={radius} style={{
            strokeDasharray: dashArray,
            strokeDashoffset: dashArray - dashArray * percent / 100,
          }}/>
          {showLabel && (
            <text className="core-progressbar-percentage" x="50" y="50" dy="0.4em" textAnchor="middle">
              {label || percentageText}
            </text>
          )}
          {!showLabel && icon && ICONS.includes(icon) && (
            <svg
              viewBox="0 0 50 100"
              className={`core-icon icon-${icon}`}
              dangerouslySetInnerHTML={{ __html:require(`../../../icons/${icon}.svg`)}}>
            </svg>
          )}
        </svg>
      );
    }
    if (marked > 0 && value < 100) {
      const full = marked >= 100;
      const start = value === 0;
      const cls = cx('core-progressbar-progress secondary', {
        'fully-marked': full,
        'start-secondary': start,
      });
      const width = full ? `calc(${100 - value}% - ${start ? '2' : '1'}px)` : `${Math.max(0, marked - value)}%`;

      secondary = <span className={cls} style={{ width }}/>;
    }
    return (
      <div className={cx('core-progressbar', className, { full: percent === 100, indeterminate })}>
        <span className="core-progressbar-container">
          <span className="core-progressbar-progress" style={{ width: percentageText }}/>
          {secondary}
        </span>
        {showLabel && <span className="core-progressbar-percentage">{label || percentageText}</span>}
      </div>
    );
  }

}
